import {
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  ViewContainerRef,
} from '@angular/core';
import { PopoverPlacement } from './popover.placement';
import { PopoverContentComponent } from '../popover-content/popover-content.component';

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[popover]',
  exportAs: 'popover',
})
export class PopoverDirective implements OnChanges {
  // -------------------------------------------------------------------------
  // Properties
  // -------------------------------------------------------------------------
  @Input('popover') public content: string | PopoverContentComponent;
  @Input() public popoverSize:
    | 'small'
    | 'medium-small'
    | 'medium'
    | 'large'
    | 'auto';
  @Input() public popoverDisabled: boolean;

  // -------------------------------------------------------------------------
  // Constructor
  @Input() public popoverAnimation: boolean;

  // -------------------------------------------------------------------------
  // Inputs / Outputs
  @Input() public popoverPlacement: PopoverPlacement;
  @Input() public popoverTitle: string;
  @Input() public popoverOnHover = true;
  @Input() public popoverCloseOnClickOutside: boolean;
  @Input() public popoverCloseOnMouseOutside: boolean;
  @Input() public popoverDismissTimeout = 0;
  @Output() public shown = new EventEmitter<PopoverDirective>();
  @Output() public hidden = new EventEmitter<PopoverDirective>();
  // -------------------------------------------------------------------------
  protected popoverContentComponent = PopoverContentComponent;
  protected popover: ComponentRef<PopoverContentComponent>;
  protected visible: boolean;

  // -------------------------------------------------------------------------
  constructor(
    protected viewContainerRef: ViewContainerRef,
    protected cdr: ChangeDetectorRef,
    protected resolver: ComponentFactoryResolver,
  ) {
  }

  // -------------------------------------------------------------------------
  // Event listeners

  // -------------------------------------------------------------------------
  @HostListener('click', ['$event'])
  public showOrHideOnClick(evt: Event): void {
    if (this.popoverOnHover) {
      return;
    }
    if (this.popoverDisabled) {
      return;
    }
    evt.stopImmediatePropagation();
    this.toggle();
  }

  @HostListener('touchend', ['$event'])
  public showOrHideOnTouch(evt: Event): void {
    evt.stopImmediatePropagation();
    if (!this.popoverOnHover) {
      return;
    }
    if (this.popoverDisabled) {
      return;
    }
    this.toggle();
  }

  @HostListener('focusin')
  @HostListener('mouseenter')
  public showOnHover(): void {
    if (!this.popoverOnHover) {
      return;
    }
    if (this.popoverDisabled) {
      return;
    }
    this.show();
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  public hideOnHover(): void {
    if (this.popoverCloseOnMouseOutside) {
      return; // don't do anything since we do not control this
    }
    if (!this.popoverOnHover) {
      return;
    }
    if (this.popoverDisabled) {
      return;
    }
    this.hide();
  }

  public ngOnChanges(changes: { [propertyName: string]: SimpleChange }): void {
    if (changes.popoverDisabled) {
      if (changes.popoverDisabled.currentValue) {
        this.hide();
      }
    }
  }

  // -------------------------------------------------------------------------
  // Public Methods
  // -------------------------------------------------------------------------
  public toggle(): void {
    if (!this.visible) {
      this.show();
    } else {
      this.hide();
    }
  }

  public show(): void {
    if (this.visible) {
      return;
    }

    this.visible = true;
    if (typeof this.content === 'string') {
      const factory = this.resolver.resolveComponentFactory(
        this.popoverContentComponent,
      );
      if (!this.visible) {
        return;
      }

      this.popover = this.viewContainerRef.createComponent(factory);
      const popover = this.popover.instance as PopoverContentComponent;
      popover.popover = this;
      popover.content = this.content as string;
      if (this.popoverPlacement !== undefined) {
        popover.placement = this.popoverPlacement;
      }
      if (this.popoverAnimation !== undefined) {
        popover.animation = this.popoverAnimation;
      }
      if (this.popoverTitle !== undefined) {
        popover.title = this.popoverTitle;
      }
      if (this.popoverCloseOnClickOutside !== undefined) {
        popover.closeOnClickOutside = this.popoverCloseOnClickOutside;
      }
      if (this.popoverCloseOnMouseOutside !== undefined) {
        popover.closeOnMouseOutside = this.popoverCloseOnMouseOutside;
      }
      if (this.popoverSize) {
        popover.size = this.popoverSize;
      }

      popover.onCloseFromOutside.subscribe(() => this.hide());
      // if dismissTimeout option is set, then this popover will be dismissed in dismissTimeout time
      if (this.popoverDismissTimeout > 0) {
        setTimeout(() => this.hide(), this.popoverDismissTimeout);
      }

      this.popover.hostView.detectChanges();
    } else {
      const popover = this.content as PopoverContentComponent;
      if (!popover) {
        return;
      }
      popover.popover = this;
      if (this.popoverPlacement !== undefined) {
        popover.placement = this.popoverPlacement;
      }
      if (this.popoverAnimation !== undefined) {
        popover.animation = this.popoverAnimation;
      }
      if (this.popoverTitle !== undefined) {
        popover.title = this.popoverTitle;
      }
      if (this.popoverCloseOnClickOutside !== undefined) {
        popover.closeOnClickOutside = this.popoverCloseOnClickOutside;
      }
      if (this.popoverCloseOnMouseOutside !== undefined) {
        popover.closeOnMouseOutside = this.popoverCloseOnMouseOutside;
      }
      if (this.popoverSize) {
        popover.size = this.popoverSize;
      }

      popover.onCloseFromOutside.subscribe(() => this.hide());
      // if dismissTimeout option is set, then this popover will be dismissed in dismissTimeout time
      if (this.popoverDismissTimeout > 0) {
        setTimeout(() => this.hide(), this.popoverDismissTimeout);
      }
      popover.show();
    }

    this.cdr.detectChanges();
    this.shown.emit(this);
  }

  public hide(): void {
    if (!this.visible) {
      return;
    }

    this.visible = false;
    if (this.popover) {
      this.popover.destroy();
    }

    if (this.content instanceof PopoverContentComponent) {
      (this.content as PopoverContentComponent).hideFromPopover();
    }

    this.cdr.detectChanges();
    this.hidden.emit(this);
  }

  public getElement(): HTMLElement {
    return this.viewContainerRef.element.nativeElement;
  }
}
